import type { WorkbenchImageToVideoPrefill } from './creationIntentRegistry'
import { cloneAlmightyPromptMentionTokens } from './almightyMention'

const MODEL_PAGE_IMAGE_TO_VIDEO_PREFILL_KEY = 'model_page_image_to_video_prefill'

type ModelPageImageToVideoPrefill = Pick<
    WorkbenchImageToVideoPrefill,
    | 'prompt'
    | 'model'
    | 'model_version'
    | 'selectedModelParentKey'
    | 'image_type'
    | 'almighty_mode'
    | 'almighty_reference_items'
    | 'almighty_prompt_tokens'
>

const isClient = () => import.meta.client && typeof window !== 'undefined'

const normalizePrefill = (
    payload: ModelPageImageToVideoPrefill
): ModelPageImageToVideoPrefill => {
    const almightyReferenceItems = Array.isArray(payload.almighty_reference_items)
        ? payload.almighty_reference_items
            .filter(item =>
                !!item &&
                typeof item.id === 'string' &&
                ['image', 'video', 'audio'].includes(item.kind || '') &&
                typeof item.url === 'string' &&
                !!item.url
            )
            .map(item => ({
                id: item.id as string,
                kind: item.kind as 'image' | 'video' | 'audio',
                url: item.url as string,
                name: item.name,
                duration: typeof item.duration === 'number'
                    ? item.duration
                    : undefined,
                loading: false,
            }))
        : []
    const normalizedPromptTokens = Array.isArray(payload.almighty_prompt_tokens)
        ? payload.almighty_prompt_tokens
            .filter(token =>
                !!token &&
                (
                    (token.type === 'text' && typeof token.text === 'string') ||
                    (
                        token.type === 'mention' &&
                        typeof token.assetId === 'string' &&
                        ['image', 'video', 'audio'].includes(token.kind || '') &&
                        typeof token.fallbackLabel === 'string'
                    )
                )
            )
            .map(token =>
                token.type === 'text'
                    ? ({
                        type: 'text' as const,
                        text: token.text as string,
                    })
                    : ({
                        type: 'mention' as const,
                        assetId: token.assetId as string,
                        kind: token.kind as 'image' | 'video' | 'audio',
                        fallbackLabel: token.fallbackLabel as string,
                    })
            )
        : []

    return {
        prompt: typeof payload.prompt === 'string' ? payload.prompt : '',
        model: typeof payload.model === 'string' ? payload.model : '',
        model_version: typeof payload.model_version === 'string'
            ? payload.model_version
            : '',
        selectedModelParentKey:
            typeof payload.selectedModelParentKey === 'string'
                ? payload.selectedModelParentKey
                : '',
        image_type: payload.image_type === 'multiple_frames'
            ? 'multiple_frames'
            : 'single_frame',
        almighty_mode: payload.almighty_mode === true,
        almighty_reference_items: almightyReferenceItems,
        almighty_prompt_tokens: cloneAlmightyPromptMentionTokens(
            normalizedPromptTokens,
            typeof payload.prompt === 'string' ? payload.prompt : ''
        ),
    }
}

export const clearModelPageImageToVideoPrefill = () => {
    if (!isClient()) return
    try {
        window.localStorage.removeItem(MODEL_PAGE_IMAGE_TO_VIDEO_PREFILL_KEY)
    } catch {
        // ignore
    }
}

export const saveModelPageImageToVideoPrefill = (
    payload: ModelPageImageToVideoPrefill
) => {
    if (!isClient()) return
    try {
        window.localStorage.setItem(
            MODEL_PAGE_IMAGE_TO_VIDEO_PREFILL_KEY,
            JSON.stringify(normalizePrefill(payload))
        )
    } catch {
        // ignore
    }
}

export const consumeModelPageImageToVideoPrefill =
    (): ModelPageImageToVideoPrefill | null => {
        if (!isClient()) return null
        try {
            const raw = window.localStorage.getItem(
                MODEL_PAGE_IMAGE_TO_VIDEO_PREFILL_KEY
            )
            if (!raw) return null
            window.localStorage.removeItem(MODEL_PAGE_IMAGE_TO_VIDEO_PREFILL_KEY)
            return normalizePrefill(JSON.parse(raw))
        } catch {
            return null
        }
    }
